import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { molarMass } from '../formula';
import {
  balanceEquation,
  parseEquation,
  subscriptFormula,
  type ParsedEquation,
  type Species,
} from './equation';
import {
  NowYouTry,
  VizCard,
  VIZ_FOCUS,
  cueHighlights,
  cueIsActive,
  usePrefersReducedMotion,
  useMountSequence,
  type VizCue,
} from '../vizShared';

// StoichiometryCalc (type 'stoichiometry') — a dimensional-analysis walkthrough:
// from a balanced equation and a known mass/moles of one species, compute another
// step by step (grams → moles → mole ratio → grams), rendering each conversion
// factor as a fraction with units cancelling. A second "limiting reagent + %
// yield" mode finds the limiting reactant and theoretical/actual yield.
// config: { equation?, given?, find?, mode?, title? }. Resilient defaults.

export interface StoichiometryConfig {
  title?: string;
  equation?: string;
  /** Index of the species used as the "given" (across reactants+products). */
  given?: number;
  /** Index of the species to solve for. */
  find?: number;
  mode?: 'single' | 'limiting';
}

interface Props {
  config?: StoichiometryConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const FALLBACK = 'CH4 + O2 -> CO2 + H2O';

export default function StoichiometryCalc({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const [equation] = useState(config.equation || FALLBACK);
  const [mode, setMode] = useState<'single' | 'limiting'>(config.mode || 'single');
  const interacted = useRef(false);

  const eq: ParsedEquation = useMemo(
    () => parseEquation(equation) ?? parseEquation(FALLBACK)!,
    [equation],
  );
  const all = useMemo(() => [...eq.reactants, ...eq.products], [eq]);
  const coeffs = useMemo(() => balanceEquation(eq) ?? all.map(() => 1), [eq, all]);
  const nR = eq.reactants.length;

  const fireComplete = () => {
    if (!interacted.current) {
      interacted.current = true;
      onComplete?.({ type: 'stoichiometry', equation });
    }
  };

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Stoichiometry · Dimensional analysis"
      title={config.title ?? 'Stoichiometry'}
      subtitle="Convert a known amount of one species into another through the balanced mole ratio."
      hint="Type a given amount and pick the species · the worked steps reveal in order."
      actions={
        <div className="inline-flex overflow-hidden rounded-sm border border-ws-border text-xs">
          {(['single', 'limiting'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => {
                fireComplete();
                setMode(m);
              }}
              className={`px-2.5 py-1 font-medium transition-colors ${VIZ_FOCUS} ${
                mode === m
                  ? 'bg-ws-accent text-ws-on-accent'
                  : 'bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
            >
              {m === 'single' ? 'Mass → Mass' : 'Limiting + Yield'}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex w-full flex-col gap-3">
        {/* Balanced equation banner */}
        <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2 font-mono text-sm text-ws-ink">
          {all.map((sp, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i === nR && <span className="mx-1 text-ws-ink-3">→</span>}
              {i > 0 && i !== nR && <span className="text-ws-ink-3">+</span>}
              <span>
                <span className="font-bold text-ws-accent">{coeffs[i] > 1 ? coeffs[i] : ''}</span>
                {subscriptFormula(sp.formula)}
              </span>
            </span>
          ))}
        </div>

        {mode === 'single' ? (
          <SingleMode
            eq={eq}
            all={all}
            coeffs={coeffs}
            config={config}
            onInteract={fireComplete}
            reduced={reduced}
            hasCue={hasCue}
            cue={cue}
          />
        ) : (
          <LimitingMode eq={eq} coeffs={coeffs} onInteract={fireComplete} />
        )}
      </div>
    </VizCard>
  );
}

// ── Mass → Mass walkthrough ─────────────────────────────────────────────────
function SingleMode({
  all,
  coeffs,
  config,
  onInteract,
  reduced,
  hasCue,
  cue,
}: {
  eq: ParsedEquation;
  all: Species[];
  coeffs: number[];
  config: StoichiometryConfig;
  onInteract: () => void;
  reduced: boolean;
  hasCue: boolean;
  cue?: VizCue;
}) {
  const [givenIdx, setGivenIdx] = useState(
    config.given != null && config.given < all.length ? config.given : 0,
  );
  const [findIdx, setFindIdx] = useState(
    config.find != null && config.find < all.length ? config.find : all.length - 1,
  );
  const [grams, setGrams] = useState(10);

  const given = all[givenIdx];
  const find = all[Math.min(findIdx, all.length - 1)];
  const mmGiven = molarMass(given.formula);
  const mmFind = molarMass(find.formula);
  const ratioGiven = coeffs[givenIdx] || 1;
  const ratioFind = coeffs[Math.min(findIdx, all.length - 1)] || 1;

  const molGiven = mmGiven > 0 ? grams / mmGiven : 0;
  const molFind = (molGiven * ratioFind) / ratioGiven;
  const gramsFind = molFind * mmFind;

  // ── Staged reveal of the 3 conversion steps (0 = none .. 3 = all). ────────
  const TOTAL_STEPS = 3;
  const [revealed, setRevealed] = useState(reduced || hasCue ? TOTAL_STEPS : 0);
  const [demoSettled, setDemoSettled] = useState(false);

  const demo = useMountSequence(
    [
      { at: 500, run: () => setRevealed(1) },
      { at: 1050, run: () => setRevealed(2) },
      { at: 1600, run: () => setRevealed(3) },
    ],
    {
      skip: reduced || hasCue,
      onSettle: () => {
        setRevealed(TOTAL_STEPS);
        setDemoSettled(true);
      },
    },
  );
  useEffect(() => {
    if (demo.done && !reduced && !hasCue) setDemoSettled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.done]);

  // Cue drives the reveal: step (discrete) or progress (continuous).
  useEffect(() => {
    if (cue?.step != null) setRevealed(Math.max(0, Math.min(TOTAL_STEPS, Math.floor(cue.step))));
    else if (cue?.progress != null)
      setRevealed(Math.round(Math.max(0, Math.min(1, cue.progress)) * TOTAL_STEPS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step, cue?.progress]);

  const highlightSet = useMemo(() => new Set(cueHighlights(cue)), [cue]);
  const spotlit = (formula: string) => highlightSet.has(formula.toLowerCase());

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
    else if (revealed < TOTAL_STEPS && !hasCue) setRevealed(TOTAL_STEPS);
  };

  const handle = (fn: () => void) => {
    cancelDemo();
    onInteract();
    fn();
  };

  return (
    <div className="relative flex flex-col gap-3">
      <NowYouTry show={demoSettled && !hasCue} />

      {/* Inputs */}
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-ws-border bg-ws-surface p-3">
        <label className="flex flex-col gap-1 text-xs text-ws-ink-3">
          Given mass (g)
          <input
            type="number"
            min={0}
            value={grams}
            onChange={(e) => handle(() => setGrams(Math.max(0, Number(e.target.value))))}
            className={`w-24 rounded-sm border border-ws-border bg-ws-tile px-2 py-1 font-mono text-sm text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ws-ink-3">
          of (known)
          <select
            value={givenIdx}
            onChange={(e) => handle(() => setGivenIdx(Number(e.target.value)))}
            className={`rounded-sm border border-ws-border bg-ws-tile px-2 py-1 text-sm text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
          >
            {all.map((sp, i) => (
              <option key={i} value={i}>
                {sp.formula}
              </option>
            ))}
          </select>
        </label>
        <span className="pb-1.5 text-ws-ink-3">→ find</span>
        <label className="flex flex-col gap-1 text-xs text-ws-ink-3">
          species
          <select
            value={Math.min(findIdx, all.length - 1)}
            onChange={(e) => handle(() => setFindIdx(Number(e.target.value)))}
            className={`rounded-sm border border-ws-border bg-ws-tile px-2 py-1 text-sm text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
          >
            {all.map((sp, i) => (
              <option key={i} value={i}>
                {sp.formula}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Dimensional-analysis train of factors */}
      <div className="overflow-x-auto rounded-md border border-ws-border bg-ws-surface-2 p-3">
        <div className="flex min-w-fit items-center gap-1.5">
          <Quantity value={fmtNum(grams)} unit={`g ${given.formula}`} solid spotlight={spotlit(given.formula)} />
          <Reveal show={revealed >= 1}>
            <Frac
              top={`1 mol ${given.formula}`}
              bottom={`${mmGiven.toFixed(2)} g ${given.formula}`}
              cancelTop={`g ${given.formula}`}
              note="molar mass"
            />
          </Reveal>
          <Reveal show={revealed >= 2}>
            <Frac
              top={`${ratioFind} mol ${find.formula}`}
              bottom={`${ratioGiven} mol ${given.formula}`}
              cancelTop={`mol ${given.formula}`}
              note="mole ratio"
            />
          </Reveal>
          <Reveal show={revealed >= 3}>
            <Frac
              top={`${mmFind.toFixed(2)} g ${find.formula}`}
              bottom={`1 mol ${find.formula}`}
              cancelTop={`mol ${find.formula}`}
              note="molar mass"
            />
          </Reveal>
          <ArrowRight size={16} className="mx-0.5 shrink-0 text-ws-ink-3" />
          <Reveal show={revealed >= 3}>
            <Quantity value={fmtNum(gramsFind)} unit={`g ${find.formula}`} result spotlight={spotlit(find.formula)} />
          </Reveal>
        </div>
      </div>

      {/* Step ledger */}
      <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
        <Reveal show={revealed >= 1}>
          <Step
            n={1}
            title="Grams → moles (given)"
            body={`${fmtNum(grams)} g ÷ ${mmGiven.toFixed(2)} g/mol = ${fmtNum(molGiven)} mol ${given.formula}`}
          />
        </Reveal>
        <Reveal show={revealed >= 2}>
          <Step
            n={2}
            title="Mole ratio"
            body={`${fmtNum(molGiven)} mol × ${ratioFind}/${ratioGiven} = ${fmtNum(molFind)} mol ${find.formula}`}
          />
        </Reveal>
        <Reveal show={revealed >= 3}>
          <Step
            n={3}
            title="Moles → grams (find)"
            body={`${fmtNum(molFind)} mol × ${mmFind.toFixed(2)} g/mol = ${fmtNum(gramsFind)} g ${find.formula}`}
          />
        </Reveal>
      </div>
    </div>
  );
}

// ── Limiting reagent + percent yield ────────────────────────────────────────
function LimitingMode({
  eq,
  coeffs,
  onInteract,
}: {
  eq: ParsedEquation;
  coeffs: number[];
  onInteract: () => void;
}) {
  const reactants = eq.reactants;
  const [grams, setGrams] = useState<number[]>(() => reactants.map(() => 10));
  const [productIdx, setProductIdx] = useState(0);
  const [actual, setActual] = useState(0);

  const handle = (fn: () => void) => {
    onInteract();
    fn();
  };

  // moles available / coefficient = "how many reaction units" each can drive.
  const perReactant = reactants.map((sp, i) => {
    const mm = molarMass(sp.formula);
    const mol = mm > 0 ? grams[i] / mm : 0;
    const coeff = coeffs[i] || 1;
    return { formula: sp.formula, mm, mol, coeff, runs: mol / coeff };
  });
  const minRuns = perReactant.reduce((m, r) => Math.min(m, r.runs), Infinity);
  const limitingIdx = perReactant.findIndex((r) => r.runs === minRuns);

  const product = eq.products[Math.min(productIdx, eq.products.length - 1)];
  const prodCoeff = coeffs[reactants.length + Math.min(productIdx, eq.products.length - 1)] || 1;
  const mmProd = molarMass(product.formula);
  const theoMol = minRuns * prodCoeff;
  const theoGrams = theoMol * mmProd;
  const pctYield = theoGrams > 0 ? (actual / theoGrams) * 100 : 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Reactant masses */}
      <div className="flex flex-wrap gap-3 rounded-md border border-ws-border bg-ws-surface p-3">
        {reactants.map((sp, i) => (
          <label key={i} className="flex flex-col gap-1 text-xs text-ws-ink-3">
            g of {subscriptFormula(sp.formula)}
            <input
              type="number"
              min={0}
              value={grams[i]}
              onChange={(e) =>
                handle(() =>
                  setGrams((g) => g.map((v, j) => (j === i ? Math.max(0, Number(e.target.value)) : v))),
                )
              }
              className={`w-24 rounded-sm border border-ws-border bg-ws-tile px-2 py-1 font-mono text-sm text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
            />
          </label>
        ))}
      </div>

      {/* Per-reactant "reaction runs" comparison */}
      <div className="rounded-md border border-ws-border bg-ws-surface-2 p-3">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
          Divide moles by coefficient — the smallest result is the limiting reagent
        </div>
        <div className="flex flex-wrap gap-2">
          {perReactant.map((r, i) => {
            const isLim = i === limitingIdx;
            return (
              <div
                key={i}
                className={`flex flex-col gap-0.5 rounded-sm border px-2.5 py-1.5 text-xs transition-all duration-300 ${
                  isLim
                    ? 'border-ws-warning/60 bg-ws-warning/10 text-ws-ink'
                    : 'border-ws-border bg-ws-tile text-ws-ink-2'
                }`}
              >
                <span className="font-mono font-semibold">{subscriptFormula(r.formula)}</span>
                <span className="text-ws-ink-3">
                  {fmtNum(r.mol)} mol ÷ {r.coeff} = <span className="font-semibold">{fmtNum(r.runs)}</span>
                </span>
                {isLim && <span className="font-semibold text-ws-warning">limiting</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Theoretical yield + percent yield */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-md border border-ws-border bg-ws-surface p-3 text-xs">
          <div className="mb-1 flex items-center gap-1.5 text-ws-ink-3">
            Theoretical yield of
            <select
              value={Math.min(productIdx, eq.products.length - 1)}
              onChange={(e) => handle(() => setProductIdx(Number(e.target.value)))}
              className={`rounded-sm border border-ws-border bg-ws-tile px-1.5 py-0.5 text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
            >
              {eq.products.map((sp, i) => (
                <option key={i} value={i}>
                  {sp.formula}
                </option>
              ))}
            </select>
          </div>
          <div className="font-mono text-ws-ink-2">
            {fmtNum(minRuns)} × {prodCoeff} mol × {mmProd.toFixed(2)} g/mol
          </div>
          <div className="mt-1 text-base font-bold text-ws-ink">{fmtNum(theoGrams)} g</div>
        </div>

        <div className="rounded-md border border-ws-border bg-ws-surface p-3 text-xs">
          <label className="mb-1 flex items-center gap-1.5 text-ws-ink-3">
            Actual yield (g)
            <input
              type="number"
              min={0}
              value={actual}
              onChange={(e) => handle(() => setActual(Math.max(0, Number(e.target.value))))}
              className={`w-20 rounded-sm border border-ws-border bg-ws-tile px-1.5 py-0.5 font-mono text-ws-ink outline-none focus:border-ws-accent ${VIZ_FOCUS}`}
            />
          </label>
          <div className="font-mono text-ws-ink-2">
            {fmtNum(actual)} ÷ {fmtNum(theoGrams)} × 100
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-base font-bold text-ws-ink">
              {Number.isFinite(pctYield) ? pctYield.toFixed(1) : '0'}%
            </span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-ws-surface-2">
              <div
                className="h-full rounded-full bg-ws-accent transition-all duration-500"
                style={{ width: `${Math.max(0, Math.min(100, pctYield))}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── small presentational helpers ────────────────────────────────────────────

// Reveal — fades/slides a step in once `show`, used for the staged walkthrough.
function Reveal({ show, children }: { show: boolean; children: React.ReactNode }) {
  return (
    <span
      className="shrink-0"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(4px)',
        transition: 'opacity 300ms ease-out, transform 300ms ease-out',
      }}
      aria-hidden={!show}
    >
      {children}
    </span>
  );
}

function Quantity({
  value,
  unit,
  solid,
  result,
  spotlight,
}: {
  value: string;
  unit: string;
  solid?: boolean;
  result?: boolean;
  spotlight?: boolean;
}) {
  return (
    <span
      className={`flex shrink-0 flex-col items-center rounded-sm border px-2.5 py-1.5 transition-all duration-300 ${
        result
          ? 'border-ws-success/60 bg-ws-success/10'
          : solid
            ? 'border-ws-accent/50 bg-ws-accent-quiet/40'
            : 'border-ws-border bg-ws-tile'
      } ${spotlight ? 'ring-2 ring-ws-accent ring-offset-1 ring-offset-ws-surface' : ''}`}
    >
      <span className="font-mono text-sm font-bold text-ws-ink">{value}</span>
      <span className="text-[10px] text-ws-ink-3">{unit}</span>
    </span>
  );
}

function Frac({
  top,
  bottom,
  cancelTop,
  note,
}: {
  top: string;
  bottom: string;
  cancelTop: string;
  note: string;
}) {
  return (
    <span className="flex shrink-0 flex-col items-center">
      <span className="text-[8px] uppercase tracking-wide text-ws-ink-4">{note}</span>
      <span className="flex flex-col items-center">
        <span className="px-1.5 text-center text-[11px] text-ws-ink-2">{markCancel(top, cancelTop)}</span>
        <span className="my-0.5 h-px w-full bg-ws-border-strong" />
        <span className="px-1.5 text-center text-[11px] text-ws-ink-2">{markCancel(bottom, cancelTop)}</span>
      </span>
    </span>
  );
}

// Strike the unit that cancels with the previous term, for the classic
// "units cancel diagonally" dimensional-analysis look.
function markCancel(text: string, unit: string) {
  const idx = text.indexOf(unit);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-ws-ink-4 line-through decoration-ws-danger">{unit}</span>
      {text.slice(idx + unit.length)}
    </>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-surface p-2.5">
      <div className="mb-1 flex items-center gap-1.5">
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-ws-accent text-[9px] font-bold text-ws-on-accent">
          {n}
        </span>
        <span className="font-medium text-ws-ink">{title}</span>
      </div>
      <div className="font-mono text-[11px] leading-relaxed text-ws-ink-2">{body}</div>
    </div>
  );
}

function fmtNum(v: number): string {
  if (!Number.isFinite(v)) return '0';
  if (v === 0) return '0';
  if (v >= 1e5 || (v < 1e-3 && v > 0)) return v.toExponential(2);
  return Number(v.toPrecision(4)).toString();
}
